import React from 'react';
import { motion } from 'framer-motion';

type StatusType = 'expired' | 'not_found' | 'error' | 'success' | 'call_office';

interface StatusScreenProps {
  type: StatusType;
  heading?: string;
  message?: string;
  /** Rendered as a tel: link on the call_office screen, when the practice has one. */
  phoneNumber?: string;
  actionLabel?: string;
  onAction?: () => void;
}

const configs: Record<StatusType, {
  icon: React.ReactNode;
  bgColor: string;
  heading: string;
  message: string;
  animation: Record<string, unknown>;
}> = {
  expired: {
    icon: (
      <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-amber-50',
    heading: 'Link Expired',
    message: 'This form link has expired. Please ask your dental office for a new link.',
    animation: { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.25 } },
  },
  not_found: {
    icon: (
      <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-gray-100',
    heading: 'Form Not Found',
    message: 'This form could not be found. Please check the link and try again.',
    animation: { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.25 } },
  },
  error: {
    icon: (
      <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    bgColor: 'bg-red-50',
    heading: 'Invalid Link',
    message: 'This link is not valid. Please use the link sent by your dental office.',
    animation: { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.25 } },
  },
  success: {
    icon: (
      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
    bgColor: 'bg-green-100',
    heading: 'Form Submitted!',
    message: 'Thank you! Your responses have been received and will be added to your record.',
    animation: { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0 }, transition: { duration: 0.3 } },
  },
  // The terminal state for a forms-only patient we could not verify, or whose form we have no
  // template for. Deliberately a dead end: the alternative — offering to register — creates a
  // duplicate chart, because everyone who receives a form link already has one.
  call_office: {
    icon: (
      <svg className="w-8 h-8 text-sky-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    bgColor: 'bg-sky-50',
    heading: "We couldn't verify your identity",
    message: "For your security we can't continue online. Please give the office a call and we'll get your forms sorted.",
    animation: { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 }, transition: { duration: 0.25 } },
  },
};

export default function StatusScreen({
  type,
  heading,
  message,
  phoneNumber,
  actionLabel,
  onAction,
}: StatusScreenProps) {
  const config = configs[type];

  return (
    <motion.div
      key={type}
      {...config.animation}
      className="text-center py-12 space-y-4"
    >
      <div className={`w-16 h-16 mx-auto ${config.bgColor} rounded-full flex items-center justify-center`}>
        {config.icon}
      </div>
      <h2 className="text-xl font-bold text-gray-900">{heading || config.heading}</h2>
      <p className="text-gray-500 text-sm max-w-xs mx-auto">
        {message || config.message}
      </p>
      {phoneNumber && (
        <a
          href={`tel:${phoneNumber.replace(/[^\d+]/g, '')}`}
          className="inline-block text-base font-semibold text-sky-600 hover:text-sky-700"
        >
          {phoneNumber}
        </a>
      )}
      {actionLabel && onAction && (
        <div>
          <button type="button" className="btn-primary max-w-xs" onClick={onAction}>
            {actionLabel}
          </button>
        </div>
      )}
    </motion.div>
  );
}
